// Domain-agnostic AND/OR/NOT condition trees — the pure half of
// ConditionGroupBuilder. Nothing here evaluates a rule against data; the only
// jobs are (a) describing the JSON shape the backend accepts, (b) mirroring
// the server's structural validation so the UI can block obviously-broken
// rules before a round-trip, and (c) rendering a tree as one plain sentence.
//
// Shape (exactly one key per node):
//   { all: [Node…] } | { any: [Node…] } | { not: Node } | { field, op, value?, label? }
// Depth ≤ MAX_DEPTH counting every node (root = 1), ≤ MAX_LEAVES conditions.

export type ValueKind = 'options' | 'text' | 'person' | 'number' | 'date' | 'none';
export interface Facet { field: string; label: string; ops: string[]; value_kind: ValueKind }
export interface FacetOption { value: string; label: string; count?: number }
export interface FacetPerson { user_id: string; name: string; department?: string }

export type LeafValue = string | string[] | number;
export interface ConditionLeaf { field: string; op: string; value?: LeafValue; label?: string }
export interface AllGroup { all: RuleNode[] }
export interface AnyGroup { any: RuleNode[] }
export interface NotNode { not: RuleNode }
export type RuleNode = AllGroup | AnyGroup | NotNode | ConditionLeaf;
export type GroupNode = AllGroup | AnyGroup;

export const MAX_DEPTH = 3;
export const MAX_LEAVES = 50;

export const isAll = (n: unknown): n is AllGroup => !!n && typeof n === 'object' && Array.isArray((n as AllGroup).all);
export const isAny = (n: unknown): n is AnyGroup => !!n && typeof n === 'object' && Array.isArray((n as AnyGroup).any);
export const isGroup = (n: unknown): n is GroupNode => isAll(n) || isAny(n);
export const isNot = (n: unknown): n is NotNode => !!n && typeof n === 'object' && 'not' in (n as object) && !!(n as NotNode).not;
export const isLeaf = (n: unknown): n is ConditionLeaf => !!n && typeof n === 'object' && !isGroup(n) && !isNot(n) && ('field' in (n as object) || 'op' in (n as object));
export const groupChildren = (g: GroupNode): RuleNode[] => (isAll(g) ? g.all : g.any);
export const makeGroup = (kind: 'all' | 'any', children: RuleNode[]): GroupNode => (kind === 'all' ? { all: children } : { any: children });
export const groupKind = (g: GroupNode): 'all' | 'any' => (isAll(g) ? 'all' : 'any');

/** Operators whose value is a list (string[]). */
export const MULTI_OPS = new Set(['in', 'not_in']);
/** Operators that carry no value at all. */
export const NO_VALUE_OPS = new Set(['is_set', 'is_not_set']);
/** Operators whose value is a positive integer. */
export const NUMBER_OPS = new Set(['within_days']);
/** Operators whose value is a YYYY-MM-DD date. */
export const DATE_OPS = new Set(['before', 'after']);
/** Operators whose value is free text regardless of the facet's value_kind. */
export const TEXT_OPS = new Set(['contains']);

export type EditorKind = 'multi' | 'single' | 'text' | 'person' | 'people' | 'number' | 'date' | 'none';

/** Which value editor a (facet, op) pair needs. */
export function editorKindFor(facet: Facet | undefined, op: string): EditorKind {
  if (!facet || !op) return 'none';
  if (NO_VALUE_OPS.has(op)) return 'none';
  if (NUMBER_OPS.has(op)) return 'number';
  if (DATE_OPS.has(op)) return 'date';
  if (TEXT_OPS.has(op)) return 'text';
  const multi = MULTI_OPS.has(op);
  switch (facet.value_kind) {
    case 'person': return multi ? 'people' : 'person';
    case 'options': return multi ? 'multi' : 'single';
    case 'number': return 'number';
    case 'date': return 'date';
    case 'none': return 'none';
    default: return multi ? 'multi' : 'text';
  }
}

// ---- validation (mirrors learning-service internal/audience.validate) -------
export type IssueCode = 'depth' | 'leaves' | 'empty_rule' | 'empty_group' | 'mixed_node' | 'missing_field' | 'missing_op' | 'missing_value' | 'bad_number' | 'bad_date';
export interface ValidationIssue { code: IssueCode; path: number[]; message: string }

const asList = (v: unknown): string[] => (Array.isArray(v) ? v.map(String).filter((s) => s.trim() !== '') : typeof v === 'string' && v.trim() ? [v] : []);

/**
 * Structural validation only — same limits and messages as the server, so a
 * rule that passes here is very unlikely to bounce with a 400. Returns [] when
 * the rule is acceptable. `maxDepth`/`maxLeaves` default to the server's.
 */
export function validateRule(node: RuleNode | null | undefined, opts: { maxDepth?: number; maxLeaves?: number } = {}): ValidationIssue[] {
  const maxDepth = opts.maxDepth ?? MAX_DEPTH;
  const maxLeaves = opts.maxLeaves ?? MAX_LEAVES;
  const issues: ValidationIssue[] = [];
  if (!node || typeof node !== 'object') return [{ code: 'empty_rule', path: [], message: 'Add at least one condition.' }];
  let leaves = 0;
  const walk = (n: RuleNode, depth: number, path: number[]) => {
    if (depth > maxDepth) { issues.push({ code: 'depth', path, message: `Rules can nest at most ${maxDepth} levels deep.` }); return; }
    const kinds = [isAll(n) && (n as AllGroup).all.length > 0, isAny(n) && (n as AnyGroup).any.length > 0, isNot(n), isLeaf(n)].filter(Boolean).length;
    if (isGroup(n) && groupChildren(n).length === 0) { issues.push({ code: 'empty_group', path, message: 'A group needs at least one condition.' }); return; }
    if (kinds !== 1) { issues.push({ code: 'mixed_node', path, message: 'Each node must be exactly one of all / any / not / a condition.' }); return; }
    if (isGroup(n)) { groupChildren(n).forEach((c, i) => walk(c, depth + 1, [...path, i])); return; }
    if (isNot(n)) { walk(n.not, depth + 1, [...path, 0]); return; }
    const leaf = n as ConditionLeaf;
    leaves += 1;
    if (leaves > maxLeaves) { issues.push({ code: 'leaves', path, message: `Rules can have at most ${maxLeaves} conditions.` }); return; }
    if (!leaf.field) { issues.push({ code: 'missing_field', path, message: 'Choose what to match on.' }); return; }
    if (!leaf.op) { issues.push({ code: 'missing_op', path, message: 'Choose how to match.' }); return; }
    if (NO_VALUE_OPS.has(leaf.op)) return;
    if (NUMBER_OPS.has(leaf.op)) {
      const num = typeof leaf.value === 'number' ? leaf.value : Number(leaf.value);
      if (!Number.isFinite(num) || num <= 0) issues.push({ code: 'bad_number', path, message: 'Enter a positive number of days.' });
      return;
    }
    if (DATE_OPS.has(leaf.op)) {
      if (typeof leaf.value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(leaf.value) || Number.isNaN(Date.parse(leaf.value))) issues.push({ code: 'bad_date', path, message: 'Enter a date (YYYY-MM-DD).' });
      return;
    }
    if (asList(leaf.value).length === 0) issues.push({ code: 'missing_value', path, message: 'Choose at least one value.' });
  };
  walk(node, 1, []);
  if (issues.length === 0 && leaves === 0) issues.push({ code: 'empty_rule', path: [], message: 'Add at least one condition.' });
  return issues;
}

/** Number of leaf conditions in a tree. */
export function countLeaves(node: RuleNode | null | undefined): number {
  if (!node) return 0;
  if (isGroup(node)) return groupChildren(node).reduce((n, c) => n + countLeaves(c), 0);
  if (isNot(node)) return countLeaves(node.not);
  return 1;
}

/** Depth of the deepest node (root = 1), the way the server counts it. */
export function ruleDepth(node: RuleNode | null | undefined): number {
  if (!node) return 0;
  if (isGroup(node)) return 1 + Math.max(0, ...groupChildren(node).map(ruleDepth));
  if (isNot(node)) return 1 + ruleDepth(node.not);
  return 1;
}

// ---- plain-English sentence -------------------------------------------------
export interface SentencePhrases {
  is: string; isNot: string; contains: string; notContains: string; under: string; notUnder: string;
  reportsTo: string; notReportsTo: string; withinDays: string; notWithinDays: string; before: string; notBefore: string;
  after: string; notAfter: string; isSet: string; isNotSet: string; and: string; or: string; orValues: string; not: string; empty: string; unknownField: string;
}
export const defaultSentencePhrases: SentencePhrases = {
  is: 'is', isNot: 'is not', contains: 'contains', notContains: 'does not contain', under: 'is under', notUnder: 'is not under',
  reportsTo: 'reports to', notReportsTo: 'does not report to', withinDays: 'is within the last {{n}} days', notWithinDays: 'is not within the last {{n}} days',
  before: 'is before {{d}}', notBefore: 'is not before {{d}}', after: 'is on or after {{d}}', notAfter: 'is not on or after {{d}}', isSet: 'is set', isNotSet: 'is not set',
  and: ', and ', or: ', or ', orValues: ' or ', not: 'not', empty: 'No conditions yet', unknownField: '…',
};
const fill = (s: string, o: Record<string, unknown>) => s.replace(/\{\{(\w+)\}\}/g, (_m, k) => String(o[k] ?? ''));

export interface SentenceOptions {
  /** value → label lookups per field (from the facets endpoint); falls back to the leaf's own label, then raw value. */
  options?: Record<string, FacetOption[]>;
  /** person id → name lookups for person-valued facets. */
  people?: FacetPerson[];
  phrases?: Partial<SentencePhrases>;
}

function leafValues(leaf: ConditionLeaf, opts: SentenceOptions): string[] {
  const raw = Array.isArray(leaf.value) ? leaf.value.map(String) : leaf.value === undefined || leaf.value === null ? [] : [String(leaf.value)];
  const optionLabel = (v: string) => opts.options?.[leaf.field]?.find((o) => o.value === v)?.label ?? opts.people?.find((p) => p.user_id === v)?.name;
  const fromOptions = raw.map(optionLabel);
  if (raw.length > 0 && fromOptions.every((x) => x !== undefined)) return fromOptions as string[];
  if (leaf.label) {
    if (raw.length > 1) {
      const parts = leaf.label.split(', ');
      if (parts.length === raw.length) return parts;
    }
    return [leaf.label];
  }
  return raw;
}

/** One leaf as "<Facet label> <op phrase> <values>"; `negated` flips the op phrase. */
export function leafToSentence(leaf: ConditionLeaf, facets: Facet[], opts: SentenceOptions = {}, negated = false): string {
  const p = { ...defaultSentencePhrases, ...(opts.phrases || {}) };
  const facet = facets.find((f) => f.field === leaf.field);
  const subject = facet?.label || leaf.field || p.unknownField;
  const values = leafValues(leaf, opts);
  const joined = values.join(p.orValues);
  const op = leaf.op;
  let neg = negated;
  let base = op;
  if (op === 'not_in') { base = 'in'; neg = !neg; }
  if (op === 'neq') { base = 'eq'; neg = !neg; }
  if (op === 'is_not_set') { base = 'is_set'; neg = !neg; }
  switch (base) {
    case 'in': case 'eq': return `${subject} ${neg ? p.isNot : p.is} ${joined}`.trim();
    case 'contains': return `${subject} ${neg ? p.notContains : p.contains} ${joined}`.trim();
    case 'subtree_of': return `${subject} ${neg ? p.notUnder : p.under} ${joined}`.trim();
    case 'reports_to': return `${subject} ${neg ? p.notReportsTo : p.reportsTo} ${joined}`.trim();
    case 'within_days': return `${subject} ${fill(neg ? p.notWithinDays : p.withinDays, { n: values[0] ?? '' })}`;
    case 'before': return `${subject} ${fill(neg ? p.notBefore : p.before, { d: values[0] ?? '' })}`;
    case 'after': return `${subject} ${fill(neg ? p.notAfter : p.after, { d: values[0] ?? '' })}`;
    case 'is_set': return `${subject} ${neg ? p.isNotSet : p.isSet}`;
    default: return `${subject} ${neg ? `${p.not} ` : ''}${op || ''} ${joined}`.replace(/\s+/g, ' ').trim();
  }
}

/**
 * Whole tree as one sentence, e.g.
 * "Department is Finance or HR, and Job title contains Director". Nested
 * groups are parenthesised; NOT over a leaf flips its phrase, NOT over a group
 * becomes "not (…)".
 */
export function ruleToSentence(node: RuleNode | null | undefined, facets: Facet[], opts: SentenceOptions = {}): string {
  const p = { ...defaultSentencePhrases, ...(opts.phrases || {}) };
  const render = (n: RuleNode, top: boolean): string => {
    if (isGroup(n)) {
      const kids = groupChildren(n);
      if (kids.length === 0) return top ? p.empty : '';
      const joined = kids.map((c) => render(c, false)).filter(Boolean).join(isAll(n) ? p.and : p.or);
      return top || kids.length === 1 ? joined : `(${joined})`;
    }
    if (isNot(n)) {
      if (isLeaf(n.not)) return leafToSentence(n.not, facets, opts, true);
      return `${p.not} ${render(n.not, false)}`;
    }
    return leafToSentence(n as ConditionLeaf, facets, opts);
  };
  if (!node) return p.empty;
  return render(node, true);
}
